import { useId, useMemo, useState } from 'react'

import Button from '@/components/ui/Button'
import ChipTabs from '@/components/ui/ChipTabs'
import SearchInput from '@/components/ui/SearchInput'
import { SECTION_CARD } from '@/components/ui/section-card-variants'
import { usePreview } from '@/features/dashboard/api/queries'
import { valueLine } from '@/features/dashboard/lib/format'
import { BLANK, STARTER_KEYS, presetDraft } from '@/features/dashboard/lib/formula'
import { presetWidget, timeWords } from '@/features/dashboard/lib/time'
import {
  MAX_WIDGETS,
  type Drawn,
  type Preset,
  type Vocabulary,
} from '@/features/mitra/api/dashboard'
import { cn } from '@/lib/utils'
import type { WidgetDraft } from '@/features/mitra/api/dashboard'
import FormulaPicture from '@/screens/dashboards/components/FormulaPicture'

/**
 * The ready-made measures, each shown with its value on the reader's own book
 * and its formula's picture before they add it -- what it is about is the
 * card's tooltip, not a paragraph -- and the free-text way in: a request
 * handed to Mitra, which builds it in the chat and asks the reader to save it
 * there.
 */

/** Live values for presets over the reader's own book, in one preview ask
 *  per set shown (the engine previews at most `MAX_WIDGETS` at once). */
function usePresetValues(presets: readonly Preset[]): Record<string, Drawn> {
  // A preset about one company has no company yet ("{symbol}"): nothing to work out.
  const drafts = useMemo(
    () =>
      presets
        .filter((p) => !p.needsSymbol)
        .slice(0, MAX_WIDGETS)
        .map(presetDraft),
    [presets],
  )
  const preview = usePreview(drafts)
  return useMemo(() => {
    const out: Record<string, Drawn> = {}
    for (const widget of preview.data?.widgets ?? []) if (widget.id) out[widget.id] = widget
    return out
  }, [preview.data])
}

/** The free-text way in: the question goes to the chat, where Mitra builds it. */
export function AskMitra({
  onAsk,
  label = 'Ask Mitra to build one',
  placeholder = 'For example: how much of my book is in banks',
}: {
  onAsk: (question: string) => void
  label?: string
  placeholder?: string
}) {
  const [text, setText] = useState('')
  const id = useId()
  const submit = (event: { preventDefault: () => void }) => {
    event.preventDefault()
    if (text.trim()) onAsk(text.trim())
  }
  return (
    <form data-role="ask-mitra" onSubmit={submit} className="flex flex-col gap-2">
      <label htmlFor={id} className="text-label-m text-foreground">
        {label}
      </label>
      <div className="flex gap-2">
        <input
          id={id}
          value={text}
          onChange={(e) => {
            setText(e.target.value)
          }}
          placeholder={placeholder}
          className="min-h-11 min-w-0 flex-1 rounded-md border border-border bg-background px-3 text-body-m text-foreground focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
        />
        <Button type="submit" variant="primary" disabled={!text.trim()}>
          Ask
        </Button>
      </div>
    </form>
  )
}

function PresetCard({
  preset,
  value,
  disabled,
  held,
  onAdd,
  onCustomise,
}: {
  preset: Preset
  value: Drawn | undefined
  disabled: boolean
  /** The companies the reader holds, offered first for a preset about one. */
  held: readonly string[]
  onAdd: (symbol?: string) => void
  onCustomise: (symbol?: string) => void
}) {
  const [symbol, setSymbol] = useState('')
  const listId = useId()
  const company = symbol.trim().toUpperCase()
  const waiting = preset.needsSymbol && company === ''
  return (
    <li
      data-role="preset-card"
      title={preset.about}
      className="flex min-w-0 flex-col gap-1 rounded-md border border-border-subtle bg-card p-3"
    >
      {/* A single figure sits beside its title; a value per holding is a line
          of its own, or it squeezes the title a word to a line. */}
      <div className={preset.kind === 'number' ? 'flex items-baseline gap-2' : 'flex flex-col'}>
        <span className="min-w-0 flex-1 text-label-l text-foreground">{preset.title}</span>
        <span
          data-role="preset-value"
          className={cn(
            'text-body-s text-foreground tabular-nums',
            preset.kind === 'number' ? 'shrink-0' : 'min-w-0 truncate',
          )}
        >
          {preset.needsSymbol ? '' : value ? valueLine(value) : '…'}
        </span>
      </div>
      {preset.time ? (
        <span data-role="preset-window" className="text-label-s text-muted-foreground">
          {timeWords(preset.time)}
        </span>
      ) : null}
      {preset.needsSymbol ? (
        <label className="flex flex-col gap-1 text-label-s text-muted-foreground">
          Company
          <input
            data-role="preset-symbol"
            value={symbol}
            list={listId}
            maxLength={20}
            placeholder={held[0] ?? 'NABIL'}
            onChange={(e) => {
              setSymbol(e.target.value)
            }}
            className="min-h-9 rounded-sm border border-border bg-background px-2 text-body-s text-foreground uppercase focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
          />
          <datalist id={listId}>
            {held.map((s) => (
              <option key={s} value={s} />
            ))}
          </datalist>
        </label>
      ) : null}
      {value?.check?.picture ? (
        <FormulaPicture
          picture={value.check.picture}
          label={value.check.explanation ?? preset.about}
        />
      ) : (
        <code
          title={preset.about}
          className="font-mono text-body-xs break-words text-muted-foreground"
        >
          {preset.formula}
        </code>
      )}
      <div className="mt-1 flex gap-2">
        <Button
          size="sm"
          variant="primary"
          disabled={disabled || waiting}
          onClick={() => {
            onAdd(preset.needsSymbol ? company : undefined)
          }}
          aria-label={`Add: ${preset.title}`}
        >
          Add
        </Button>
        <Button
          size="sm"
          variant="secondary"
          disabled={waiting}
          onClick={() => {
            onCustomise(preset.needsSymbol ? company : undefined)
          }}
          aria-label={`Customise: ${preset.title}`}
        >
          Customise
        </Button>
      </div>
    </li>
  )
}

/** The gallery: search, categories, and every measure's value on the reader's book. */
export default function Gallery({
  vocabulary,
  disabled,
  onAdd,
  onCustomise,
  onClose,
  onAsk,
}: {
  vocabulary: Vocabulary
  disabled: boolean
  onAdd: (preset: Preset, symbol?: string) => void
  onCustomise: (draft: WidgetDraft) => void
  /** Where the gallery stands alone; inside the builder it has none. */
  onClose?: (() => void) | undefined
  onAsk: ((question: string) => void) | null
}) {
  const groups = useMemo(() => [...new Set(vocabulary.presets.map((p) => p.group))], [vocabulary])
  const [group, setGroup] = useState<string>(groups[0] ?? '')
  const [query, setQuery] = useState('')
  const shown = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (q) {
      return vocabulary.presets.filter((p) =>
        `${p.title} ${p.about} ${p.formula} ${p.group}`.toLowerCase().includes(q),
      )
    }
    return vocabulary.presets.filter((p) => p.group === group)
  }, [vocabulary, group, query])
  const values = usePresetValues(shown)
  return (
    <section
      data-role="dash-gallery"
      aria-label="Ready-made measures"
      className={cn(onClose ? SECTION_CARD : '', 'flex min-w-0 flex-col gap-3')}
    >
      <div className="flex flex-wrap items-center gap-2">
        <h2 className="flex-1 text-h-xs text-foreground">Ready-made measures</h2>
        <Button
          size="sm"
          variant="secondary"
          onClick={() => {
            onCustomise(BLANK)
          }}
        >
          Write your own
        </Button>
        {onClose ? (
          <Button size="sm" variant="quiet" onClick={onClose}>
            Close
          </Button>
        ) : null}
      </div>
      <SearchInput
        value={query}
        onChange={(e) => {
          setQuery(e.target.value)
        }}
        aria-label="Search measures"
        placeholder="Search measures"
      />
      {!query.trim() && groups.length > 0 ? (
        <ChipTabs
          options={groups.map((g) => ({ value: g, label: g }))}
          value={group}
          onChange={setGroup}
          ariaLabel="Measure categories"
        />
      ) : null}
      <ul className="grid grid-cols-1 gap-2 @lg:grid-cols-2">
        {shown.map((preset) => (
          <PresetCard
            key={preset.key}
            preset={preset}
            value={values[preset.key]}
            disabled={disabled}
            held={vocabulary.held.symbol}
            onAdd={(symbol) => {
              onAdd(preset, symbol)
            }}
            onCustomise={(symbol) => {
              onCustomise(presetWidget(preset, symbol))
            }}
          />
        ))}
      </ul>
      {onAsk ? <AskMitra onAsk={onAsk} /> : null}
    </section>
  )
}

/**
 * For a reader with no idea what to ask: ready-made measures not yet on this
 * dashboard, one tap each. The same presets the Add-a-measure dialog lists.
 */
export function Suggestions({
  vocabulary,
  have,
  disabled,
  onAdd,
}: {
  vocabulary: Vocabulary
  /** The titles and formulas already on the dashboard. */
  have: readonly string[]
  disabled: boolean
  onAdd: (preset: Preset) => void
}) {
  // One tap each, so never a preset that first needs a company picked.
  const fresh = vocabulary.presets
    .filter((preset) => !preset.needsSymbol)
    .filter((preset) => !have.includes(preset.formula) && !have.includes(preset.title))
    .slice(0, 5)
  if (fresh.length === 0) return null
  return (
    <div data-role="dash-suggestions" className="flex flex-col gap-2">
      <p className="text-label-m text-foreground">Suggested measures</p>
      <ul className="flex flex-wrap gap-2">
        {fresh.map((preset) => (
          <li key={preset.key}>
            <Button
              size="sm"
              variant="secondary"
              disabled={disabled}
              onClick={() => {
                onAdd(preset)
              }}
            >
              {preset.title}
            </Button>
          </li>
        ))}
      </ul>
    </div>
  )
}

/** The empty dashboard's first step: three measures to keep with one tap,
 *  each already worked out on the reader's book. */
export function Starters({
  vocabulary,
  disabled,
  onAdd,
}: {
  vocabulary: Vocabulary
  disabled: boolean
  onAdd: (preset: Preset) => void
}) {
  const presets = useMemo(
    () =>
      STARTER_KEYS.map((key) => vocabulary.presets.find((p) => p.key === key)).filter(
        (p): p is Preset => p !== undefined,
      ),
    [vocabulary],
  )
  const values = usePresetValues(presets)
  if (presets.length === 0) return null
  return (
    <div data-role="dash-starters" className="flex flex-col gap-2">
      <p className="text-label-m text-foreground">Start with one of these</p>
      <ul className="flex flex-col gap-2">
        {presets.map((preset) => {
          const value = values[preset.key]
          return (
            <li
              key={preset.key}
              className="flex min-w-0 items-center gap-3 rounded-md border border-border-subtle bg-card p-3"
            >
              <span className="min-w-0 flex-1">
                <span className="block text-label-l text-foreground">{preset.title}</span>
                <span className="block truncate text-body-s text-muted-foreground tabular-nums">
                  {value ? valueLine(value) : '…'}
                </span>
              </span>
              <Button
                size="sm"
                variant="primary"
                disabled={disabled}
                onClick={() => {
                  onAdd(preset)
                }}
                aria-label={`Add: ${preset.title}`}
              >
                Add
              </Button>
            </li>
          )
        })}
      </ul>
    </div>
  )
}
